import { useEffect, useReducer } from "react";
import i18n from "@/i18n";
import { loadWithRecovery } from "@/lib/lazyRoute";

// engine y advisors no viajan en el bundle inicial (revision del bloque E, I-1: +69 kB gzip para todo
// visitante de la web y de la app nativa). Cada catalogo es un chunk propio que se pide la primera vez que
// una pantalla del Engine, /asesores o la variante Engine del registro y el acceso lo necesitan.
// Sin backend de i18next: changeLanguage sigue siendo sincrono y quien cambia de idioma dentro del Engine
// trae antes el catalogo (useAppLanguage, LanguageSelect). initialBundle.test.js vigila que no vuelvan.
// Ojo: nada puede traducir al importar un modulo (constantes con t()): el catalogo aun no esta.
// es/public y es/shell van estaticos en index.js; el resto de los namespaces B2C por aqui.
const LOADERS = {
    "es/engine": () => import(/* webpackChunkName: "i18n-es-engine" */ "@/locales/es/engine.json"),
    "en/engine": () => import(/* webpackChunkName: "i18n-en-engine" */ "@/locales/en/engine.json"),
    "es/advisors": () => import(/* webpackChunkName: "i18n-es-advisors" */ "@/locales/es/advisors.json"),
    "en/advisors": () => import(/* webpackChunkName: "i18n-en-advisors" */ "@/locales/en/advisors.json"),
    "en/public": () => import(/* webpackChunkName: "i18n-en-public" */ "@/locales/en/public.json"),
    "es/auth": () => import(/* webpackChunkName: "i18n-es-auth" */ "@/locales/es/auth.json"),
    "en/auth": () => import(/* webpackChunkName: "i18n-en-auth" */ "@/locales/en/auth.json"),
    "en/shell": () => import(/* webpackChunkName: "i18n-en-shell" */ "@/locales/en/shell.json"),
    "es/portfolio": () => import(/* webpackChunkName: "i18n-es-portfolio" */ "@/locales/es/portfolio.json"),
    "en/portfolio": () => import(/* webpackChunkName: "i18n-en-portfolio" */ "@/locales/en/portfolio.json"),
    "es/operations": () => import(/* webpackChunkName: "i18n-es-operations" */ "@/locales/es/operations.json"),
    "en/operations": () => import(/* webpackChunkName: "i18n-en-operations" */ "@/locales/en/operations.json"),
    "es/analysis": () => import(/* webpackChunkName: "i18n-es-analysis" */ "@/locales/es/analysis.json"),
    "en/analysis": () => import(/* webpackChunkName: "i18n-en-analysis" */ "@/locales/en/analysis.json"),
    "es/account": () => import(/* webpackChunkName: "i18n-es-account" */ "@/locales/es/account.json"),
    "en/account": () => import(/* webpackChunkName: "i18n-en-account" */ "@/locales/en/account.json"),
    "es/legal": () => import(/* webpackChunkName: "i18n-es-legal" */ "@/locales/es/legal.json"),
    "en/legal": () => import(/* webpackChunkName: "i18n-en-legal" */ "@/locales/en/legal.json"),
};

export const ENGINE_NS = ["engine"];
// Asesores pinta tambien textos del Engine (nombres de plan, metricas y graficos del ejemplo).
export const ADVISORS_PAGE_NS = ["advisors", "engine"];
export const SHELL_NS = ["shell"];
export const PUBLIC_NS = ["public"];

const langsOf = (lng) => [...new Set(Array.isArray(lng) ? lng : [lng])];
const pairs = (namespaces, lng) => langsOf(lng).flatMap((l) => namespaces.map((ns) => [l, ns]));

export const hasCatalogs = (namespaces, lng) => pairs(namespaces, lng).every(([l, ns]) => i18n.hasResourceBundle(l, ns));

const pending = new Map();

function loadCatalog(lng, ns) {
    if (i18n.hasResourceBundle(lng, ns)) return Promise.resolve();
    const key = `${lng}/${ns}`;
    if (!LOADERS[key]) return Promise.reject(new Error(`No hay catalogo ${key}`));
    if (!pending.has(key)) {
        const load = loadWithRecovery(LOADERS[key]).then(
            (mod) => { i18n.addResourceBundle(lng, ns, mod.default || mod, true, true); },
        ).finally(() => pending.delete(key));
        pending.set(key, load);
    }
    return pending.get(key);
}

export const loadCatalogs = (namespaces, lng) =>
    Promise.all(pairs(namespaces, lng).map(([l, ns]) => loadCatalog(l, ns))).then(() => undefined);

// Para lazyRoute: el chunk de la pagina y sus catalogos se piden a la vez, no uno detras de otro.
// `lng` puede ser una funcion: se lee al pedir la ruta, no al cargar App.js.
export const withCatalogs = (namespaces, lng, factory) => () =>
    Promise.all([factory(), loadCatalogs(namespaces, typeof lng === "function" ? lng() : lng)]).then(([mod]) => mod);

const failures = new Map();

// Suspende (Suspense mas cercano) hasta tener los catalogos: ni claves en crudo ni textos vacios.
// Si no se pueden traer, el error sube a ErrorBoundary, como un chunk de ruta que no carga.
export function useCatalogs(namespaces, lng) {
    if (hasCatalogs(namespaces, lng)) return;
    const id = pairs(namespaces, lng).join(";");
    if (failures.has(id)) {
        const err = failures.get(id);
        failures.delete(id);
        throw err;
    }
    throw loadCatalogs(namespaces, lng).catch((err) => { failures.set(id, err); });
}

// Sin suspender, para lo que no puede esperar (el menu de AppLayout): dice si ya estan y, con `wanted`,
// los pide. `namespaces` tiene que ser estable (una constante del modulo).
export function useCatalogsReady(namespaces, lng, wanted = true) {
    const ready = hasCatalogs(namespaces, lng);
    const [, rerender] = useReducer((n) => n + 1, 0);
    useEffect(() => {
        if (!wanted || ready) return undefined;
        let live = true;
        loadCatalogs(namespaces, lng).then(() => live && rerender(), () => {});
        return () => { live = false; };
    }, [namespaces, lng, wanted, ready]);
    return ready;
}
